import { MouseEvent, ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import DescriptionIcon from "@mui/icons-material/Description";
import EventIcon from "@mui/icons-material/Event";
import HearingIcon from "@mui/icons-material/Hearing";
import LaunchIcon from "@mui/icons-material/Launch";
import PaidIcon from "@mui/icons-material/Paid";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { misc, neutral, primary } from "app/themes/colors";
import type { Patient } from "services/types/patients.type";
import { formatMoney } from "utils/common";

import { LILAC, PLUM_600, PLUM_900 } from "./dialstack-softphone-widget.styles";
import {
  activeTags,
  appointmentContext,
  balanceDue,
  hearingAidLabel,
  identityLine,
  paymentSourceLabel,
  pendingFormsCount,
  providerLine,
  providerNames
} from "./softphone-patient-context.util";
import SoftphonePatientTags from "./softphone-patient-tags.component";

type ChipTone = { bgcolor: string; color: string };

const TONES = {
  teal: { bgcolor: misc.lightGreen, color: primary[500] },
  amber: { bgcolor: misc.lightYellow, color: misc.warningText },
  lilac: { bgcolor: misc.highlightPurple, color: misc.purpleText },
  neutral: { bgcolor: neutral[100], color: neutral[800] }
} satisfies Record<string, ChipTone>;

function ContextChip({
  icon,
  label,
  tone,
  testId
}: {
  icon: ReactNode;
  label: string;
  tone: ChipTone;
  testId: string;
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.5}
      data-testid={testId}
      sx={{
        px: 1.25,
        py: 0.375,
        borderRadius: "999px",
        fontSize: 13,
        fontWeight: 600,
        lineHeight: 1.3,
        ...tone
      }}
    >
      {icon}
      <span>{label}</span>
    </Stack>
  );
}

const CHIP_ICON_SX = { fontSize: 13 };

// Tags are the practice's own shorthand for a patient, so they sit right
// under the name; past this many the rest fold into a "+N" chip.
const MAX_TAGS = 4;

const ACTION_SX = {
  fontSize: 12,
  fontWeight: 600,
  textTransform: "none",
  px: 1.25,
  py: 0.25,
  minWidth: 0,
  whiteSpace: "nowrap",
  color: PLUM_600,
  borderColor: LILAC,
  "&:hover": { borderColor: PLUM_600, bgcolor: misc.lightLavender }
} as const;

// The workbench's actions are the things a caller usually wants once the
// call is up, so they get 40px targets at 14px.
const GRID_ACTION_SX = {
  ...ACTION_SX,
  fontSize: 14,
  fontWeight: 700,
  justifyContent: "flex-start",
  px: 1.5,
  minHeight: 40,
  borderRadius: "10px",
  bgcolor: misc.lightLavender
} as const;

const PAY_ACTION_SX = {
  color: primary[500],
  borderColor: misc.lightGreen,
  bgcolor: misc.lightGreen,
  "&:hover": { borderColor: primary[500], bgcolor: misc.lightGreen }
} as const;

// The hero for the common case of a single matched patient on a ringing
// call: the photo (initials fallback) with a soft ringing pulse beside the
// name, identity line and provider, then tags, glanceable context chips, and
// one-click jumps into the chart. Multi-patient matches stay on the compact
// SoftphonePatientRow. Everything sits left, the way the chart reads
// (ENG-2226): the pop's header above it already centres the call's state.
//
// The photo pulses only while the call is `ringing`.
//
// `actions="grid"` is the workbench's 2×2 of 40px targets — the three jumps
// plus Take payment, which opens the new sale and payment form in the chart
// (ENG-2226). The ringing pop keeps the one-line row.
export default function SoftphonePatientHero({
  patient,
  caption,
  ringing = false,
  actions = "row"
}: {
  patient: Patient;
  caption?: string;
  ringing?: boolean;
  actions?: "row" | "grid";
}) {
  const navigate = useNavigate();
  const href = `/patients/${patient.id}`;
  const context = appointmentContext(patient);
  const identity = identityLine(patient);
  const balance = balanceDue(patient);
  const pendingForms = pendingFormsCount(patient);
  const paymentSource = paymentSourceLabel(patient);
  const hearingAids = hearingAidLabel(patient);
  const tags = activeTags(patient);
  const providers = providerNames(patient);
  const name =
    patient.fullName ||
    `${patient.first_name ?? ""} ${patient.last_name ?? ""}`.trim();

  const handleNameClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    navigate(href);
  };

  const chips = [
    context && (
      <ContextChip
        key="appointment"
        icon={<EventIcon sx={CHIP_ICON_SX} />}
        label={[context.shortWhen, context.details].filter(Boolean).join(" · ")}
        tone={TONES.teal}
        testId="hero-chip-appointment"
      />
    ),
    balance > 0 && (
      <ContextChip
        key="balance"
        icon={<PaidIcon sx={CHIP_ICON_SX} />}
        label={`${formatMoney(balance, 2)} due`}
        tone={TONES.amber}
        testId="hero-chip-balance"
      />
    ),
    pendingForms > 0 && (
      <ContextChip
        key="forms"
        icon={<DescriptionIcon sx={CHIP_ICON_SX} />}
        label={`${pendingForms} form${pendingForms > 1 ? "s" : ""} pending`}
        tone={TONES.amber}
        testId="hero-chip-forms"
      />
    ),
    <ContextChip
      key="payment"
      icon={<VerifiedUserIcon sx={CHIP_ICON_SX} />}
      label={paymentSource}
      tone={TONES.neutral}
      testId="hero-chip-payment"
    />,
    hearingAids && (
      <ContextChip
        key="hearing"
        icon={<HearingIcon sx={CHIP_ICON_SX} />}
        label={hearingAids}
        tone={TONES.lilac}
        testId="hero-chip-hearing"
      />
    )
  ].filter(Boolean);

  const avatarSize = 56;
  const justify = "flex-start";
  const pulseSpread = 12;

  const avatar = (
    <Avatar
      src={patient.photo || undefined}
      sx={{
        width: avatarSize,
        height: avatarSize,
        flexShrink: 0,
        fontSize: 20,
        fontWeight: 600,
        bgcolor: LILAC,
        color: PLUM_900,
        boxShadow: `0 0 0 3px ${LILAC}`,
        animation: ringing ? "suno-hero-ring 2s ease-out infinite" : "none",
        "@keyframes suno-hero-ring": {
          "0%": {
            boxShadow: `0 0 0 3px ${LILAC}, 0 0 0 3px hsla(272, 45%, 66%, 0.45)`
          },
          "70%": {
            boxShadow: `0 0 0 3px ${LILAC}, 0 0 0 ${pulseSpread}px hsla(272, 45%, 66%, 0)`
          },
          "100%": {
            boxShadow: `0 0 0 3px ${LILAC}, 0 0 0 0 hsla(272, 45%, 66%, 0)`
          }
        },
        "@media (prefers-reduced-motion: reduce)": { animation: "none" }
      }}
    >
      {(patient.first_name?.[0] ?? "") + (patient.last_name?.[0] ?? "")}
    </Avatar>
  );

  const identityBlock = (
    <Box sx={{ minWidth: 0 }}>
      <Link
        href={href}
        onClick={handleNameClick}
        underline="hover"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          fontSize: 17,
          fontWeight: 700,
          lineHeight: 1.25,
          overflowWrap: "anywhere",
          color: PLUM_600
        }}
      >
        {name}
        <LaunchIcon sx={{ fontSize: 15 }} />
      </Link>
      {identity && (
        <Typography sx={{ fontSize: 12, color: neutral[800], mt: 0.25 }}>
          {identity}
        </Typography>
      )}
      {providers.length > 0 && (
        <Typography
          data-testid="hero-provider"
          sx={{ fontSize: 12, color: neutral[800], mt: 0.25 }}
        >
          Provider:{" "}
          <Box component="span" sx={{ fontWeight: 600, color: PLUM_900 }}>
            {providerLine(providers)}
          </Box>
        </Typography>
      )}
    </Box>
  );

  return (
    <Box
      data-testid="incoming-call-patient-hero"
      sx={{ py: 0.75, textAlign: "left" }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        // Clears the avatar's 3px ring, so it lines up with the row below.
        sx={{ pl: 0.5 }}
      >
        {avatar}
        {identityBlock}
      </Stack>
      {tags.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <SoftphonePatientTags
            tags={tags}
            limit={MAX_TAGS}
            justify={justify}
          />
        </Box>
      )}
      <Stack
        direction="row"
        spacing={0}
        useFlexGap
        flexWrap="wrap"
        justifyContent={justify}
        sx={{ gap: 0.75, mt: 1.25 }}
      >
        {chips}
      </Stack>
      {caption && (
        <Typography
          sx={{ fontSize: 12, color: neutral[800], fontStyle: "italic", mt: 1 }}
        >
          {caption}
        </Typography>
      )}
      {actions === "grid" ? (
        <Box
          data-testid="hero-action-grid"
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 1,
            mt: 1.25,
            pt: 1.25,
            borderTop: `1px solid ${neutral[100]}`
          }}
        >
          <Button
            variant="outlined"
            sx={GRID_ACTION_SX}
            onClick={() => navigate(href)}
          >
            Open chart
          </Button>
          <Button
            variant="outlined"
            sx={GRID_ACTION_SX}
            onClick={() => navigate(`${href}/appointments`)}
          >
            Appointments
          </Button>
          <Button
            variant="outlined"
            sx={GRID_ACTION_SX}
            onClick={() => navigate(`${href}/billing`)}
          >
            Billing
          </Button>
          <Button
            variant="outlined"
            sx={[GRID_ACTION_SX, PAY_ACTION_SX]}
            onClick={() => navigate(`${href}/billing/new`)}
          >
            Take payment
          </Button>
        </Box>
      ) : (
        <Stack
          direction="row"
          spacing={0.75}
          justifyContent={justify}
          sx={{
            mt: 1.25,
            pt: 1.25,
            borderTop: `1px solid ${neutral[100]}`
          }}
        >
          <Button
            size="small"
            variant="outlined"
            sx={ACTION_SX}
            onClick={() => navigate(href)}
          >
            Open chart
          </Button>
          <Button
            size="small"
            variant="outlined"
            sx={ACTION_SX}
            onClick={() => navigate(`${href}/appointments`)}
          >
            Appointments
          </Button>
          <Button
            size="small"
            variant="outlined"
            sx={ACTION_SX}
            onClick={() => navigate(`${href}/billing`)}
          >
            Billing
          </Button>
        </Stack>
      )}
    </Box>
  );
}
